<template>
<b-modal
    id="split-ticket-modal"
    title="Split Ticket"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    centered
  >
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="container-fluid px-3 d-flex">
        <div class="flex-grow-1">
          <div class="btn-group mr-2">
                                  <button
              type="button"
              class="btn"
              :class="{ 'btn-itg-dark': currentStep === 1, 'btn-itg': currentStep !== 1 }"
              @click="goToStep(1)"
              :disabled="isSaving"
            >
              <i class="fa fa-info-circle mr-2"></i>
              Basic Info
            </button>
            <button
              type="button"
              class="btn"
              :class="{ 'btn-itg-dark': currentStep === 2, 'btn-itg': currentStep !== 2 }"
              @click="goToStep(2)"
              :disabled="isSaving"
            >
              <i class="fa fa-tasks mr-2"></i>
              Tasks
            </button>
            <button
              type="button"
              class="btn"
              :class="{ 'btn-itg-dark': currentStep === 3, 'btn-itg': currentStep !== 3 }"
              @click="goToStep(3)"
              :disabled="isSaving"
            >
              <i class="fa fa-stream mr-2"></i>
              Timeline
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Body -->
    <div class="split-ticket-form px-4 pt-4 pb-0 w-100" style="border-radius:0;">
      <!-- Step 1: Basic Information -->
      <div v-if="currentStep === 1" class="step-content">
        <h5 class="mb-3 font-weight-bold">Basic Information</h5>
        <div class="form-group">
          <label for="new-subject">New Ticket Subject *</label>
          <input
            id="new-subject"
            v-model="form.subject"
            type="text"
            class="form-control"
            maxlength="255"
            placeholder="Enter new ticket subject"
            :class="{ 'is-invalid': errors.subject }"
            @input="clearError('subject')"
          />
          <div class="invalid-feedback" v-if="errors.subject">
            {{ errors.subject[0] }}
          </div>
        </div>

        <div class="form-group">
          <label for="new-description">New Ticket Description *</label>
          <textarea
            id="new-description"
            v-model="form.description"
            class="form-control"
            rows="4"
            placeholder="Enter new ticket description"
            :class="{ 'is-invalid': errors.description }"
            @input="clearError('description')"
          ></textarea>
          <div class="invalid-feedback" v-if="errors.description">
            {{ errors.description[0] }}
          </div>
        </div>
      </div>

      <!-- Step 2: Tasks -->
      <div v-if="currentStep === 2" class="step-content">
        <h5 class="mb-3 font-weight-bold">Copy Tasks</h5>
        <div v-if="ticket.tasks && ticket.tasks.length" class="tasks-section">
          <div class="form-check mb-3">
            <input
              class="form-check-input"
              type="checkbox"
              id="copy-tasks"
              v-model="form.copy_tasks"
              @change="handleCopyToggle('tasks')"
            />
            <label class="form-check-label" for="copy-tasks">
              <strong>Copy tasks from original ticket</strong> ({{ ticket.tasks.length }} available)
            </label>
          </div>
          <div v-if="form.copy_tasks" class="task-items">
            <div class="form-check" v-for="task in ticket.tasks" :key="task.id">
              <input
                class="form-check-input"
                type="checkbox"
                :id="`task-${task.id}`"
                v-model="form.selected_task_ids"
                :value="task.id"
                @change="updateTaskCopyFlag"
              />
              <label class="form-check-label" :for="`task-${task.id}`">
                <strong>{{ task.title }}</strong><br>
                <small class="text-muted">{{ task.description }}</small>
              </label>
            </div>
          </div>
          
          <!-- Task selection summary -->
          <div v-if="form.copy_tasks" class="mt-3 p-3 bg-light rounded">
            <small class="text-muted">
              <i class="fa fa-tasks text-primary"></i>
              Tasks: {{ form.selected_task_ids.length }} of {{ ticket.tasks.length }} selected
            </small>
          </div>
        </div>
        <div v-else class="text-muted">
          No tasks available to copy.
        </div>
      </div>

      <!-- Step 3: Timeline Elements -->
      <div v-if="currentStep === 3" class="step-content d-flex flex-column h-100">
        <h5 class="mb-3 font-weight-bold">Select Timeline Elements To Copy To New Ticket</h5>
        
        <!-- Summary of selections -->
        <div v-if="false" class="selection-summary mb-4 p-3 bg-light rounded">
          <h6 class="mb-2">Summary of selections:</h6>
          <div class="row">
            <div class="col-md-6" v-for="(count, type) in getSelectionSummary()" :key="type">
              <small class="text-muted">
                <i :class="getIconForType(type)"></i>
                {{ getTypeLabel(type) }}: {{ count }} selected
              </small>
            </div>
          </div>
        </div>

        <div class="timeline-list-scroll flex-grow-1">
          <div class="timeline-elements">
            <div v-for="(item, i) in timelineFeed" :key="i" class="timeline-item-wrapper d-flex align-items-start p-0" style="background:none; border:none;">
              <label :for="`timeline-${item.type}-${item.object.id}`" class="d-flex align-items-start w-100 h-100 m-0 pl-4" style="cursor:pointer; padding-left: 1.5rem;">
                <input
                  class="mt-2 mr-3"
                  type="checkbox"
                  :id="`timeline-${item.type}-${item.object.id}`"
                  :checked="isItemSelected(item.type, item.object.id)"
                  @change="toggleItemSelection(item.type, item.object.id)"
                  style="flex-shrink:0; margin-right: 1.5rem;"
                />
                <div class="flex-grow-1">
                  <component
                    :is="getTimelineComponent(item.type)"
                    v-bind="getTimelineProps(item)"
                    :disable-actions="true"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end align-items-center">
        <div class="d-flex gap-2">
          <button 
            type="button" 
            class="btn btn-itg" 
            @click="previousStep"
            :disabled="currentStep === 1 || isSaving"
          >
            <i class="fa fa-chevron-left mr-1"></i>
            Previous
          </button>

          <button
            type="button"
            class="btn btn-itg"
            @click="nextStep"
            :disabled="currentStep === 3 || isSaving"
          >
            Next
            <i class="fa fa-chevron-right ml-1"></i>
          </button>

          <button
            type="button"
            class="btn btn-itg-dark"
            @click="splitTicket"
            :disabled="isSaving"
          >
            <span v-if="isSaving">
              <i class="fa fa-spinner fa-spin mr-1"></i>
              Splitting...
            </span>
            <span v-else> Split Ticket </span>
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Forms from "../../../../../mixins/Forms";
import Dates from "../../../../../mixins/Dates";
import TicketNote from "../widgets/TicketNote.vue";
import TicketForwardingLog from "../widgets/TicketForwardingLog.vue";
import TicketReply from "../widgets/TicketReply.vue";
import InboundEmail from "../widgets/InboundEmail.vue";
import TicketSmsLog from "../widgets/TicketSmsLog.vue";
import TicketWhatsappLog from "../widgets/TicketWhatsappLog.vue";
import TicketInboundTextMessage from "../widgets/TicketInboundTextMessage.vue";
import TicketInboundWhatsappMessage from "../widgets/TicketInboundWhatsappMessage.vue";
import TicketExpense from "../widgets/TicketExpense.vue";

export default {
  name: "SplitTicketModal",
  mixins: [Forms, Dates],
  components: {
    TicketNote,
    TicketForwardingLog,
    TicketReply,
    InboundEmail,
    TicketSmsLog,
    TicketWhatsappLog,
    TicketInboundTextMessage,
    TicketInboundWhatsappMessage,
    TicketExpense
  },
  data() {
    return {
      currentStep: 1,
      isSaving: false,
      errors: {},
      form: {
        subject: '',
        description: '',
        copy_contacts: true,
        copy_tasks: false,
        copy_expenses: false,
        copy_notes: false,
        copy_replies: false,
        copy_forwarding_logs: false,
        copy_sms_logs: false,
        copy_whatsapp_logs: false,
        copy_inbound_emails: false,
        copy_inbound_text_messages: false,
        copy_inbound_whatsapp_messages: false,
        selected_contact_ids: [],
        selected_task_ids: [],
        selected_expense_ids: [],
        selected_note_ids: [],
        selected_reply_ids: [],
        selected_forwarding_log_ids: [],
        selected_sms_log_ids: [],
        selected_whatsapp_log_ids: [],
        selected_inbound_email_ids: [],
        selected_inbound_text_message_ids: [],
        selected_inbound_whatsapp_message_ids: []
      }
    };
  },
  mounted() {
    // Listen for modal show event
    this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
      if (modalId === 'split-ticket-modal') {
        this.resetForm();
      }
    });
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('bv::modal::shown');
  },
  computed: {
    ...mapGetters('ticketViewer', {
      ticket: 'ticket',
      expenses: 'expenses'
    }),

    canProceedToNextStep() {
      switch (this.currentStep) {
        case 1:
          return this.form.subject && this.form.description;
        case 2:
        case 3:
          return true; // These steps are optional
        default:
          return false;
      }
    },

    timelineFeed() {
      let feed = [];

      // Add notes
      if (this.ticket.ticket_notes) {
        this.ticket.ticket_notes.forEach(note => {
          feed.push({
            type: 'note',
            object: this.buildTimelineObject(note)
          });
        });
      }

      // Add forwarding logs
      if (this.ticket.ticket_forwarding_logs) {
        this.ticket.ticket_forwarding_logs.forEach(log => {
          feed.push({
            type: 'forwarding_log',
            object: this.buildTimelineObject(log)
          });
        });
      }

      // Add logged emails
      if (this.ticket.logged_emails) {
        this.ticket.logged_emails.forEach(email => {
          feed.push({
            type: 'logged_email',
            object: this.buildTimelineObject(email)
          });
        });
      }

      // Add replies
      if (this.ticket.ticket_replies) {
        this.ticket.ticket_replies.forEach(reply => {
          feed.push({
            type: 'reply',
            object: this.buildTimelineObject(reply)
          });
        });
      }

      // Add inbound emails
      if (this.ticket.inbound_emails) {
        this.ticket.inbound_emails.forEach(email => {
          feed.push({
            type: 'inbound_email',
            object: this.buildTimelineObject(email)
          });
        });
      }

      // Add SMS logs
      if (this.ticket.ticket_sms_logs) {
        this.ticket.ticket_sms_logs.forEach(log => {
          feed.push({
            type: 'sms_log',
            object: this.buildTimelineObject(log)
          });
        });
      }

      // Add WhatsApp logs
      if (this.ticket.ticket_whatsapp_logs) {
        this.ticket.ticket_whatsapp_logs.forEach(log => {
          feed.push({
            type: 'whatsapp_log',
            object: this.buildTimelineObject(log)
          });
        });
      }

      // Add inbound text messages
      if (this.ticket.inbound_text_messages) {
        this.ticket.inbound_text_messages.forEach(message => {
          feed.push({
            type: 'inbound_text_message',
            object: this.buildTimelineObject(message)
          });
        });
      }

      // Add inbound WhatsApp messages
      if (this.ticket.inbound_whatsapp_messages) {
        this.ticket.inbound_whatsapp_messages.forEach(message => {
          feed.push({
            type: 'inbound_whatsapp_message',
            object: this.buildTimelineObject(message)
          });
        });
      }

      // Add expenses
      if (this.expenses) {
        this.expenses.forEach(expense => {
          feed.push({
            type: 'expense',
            object: this.buildTimelineObject(expense)
          });
        });
      }

      return _.sortBy(feed, 'object.created_at').reverse();
    }


  },
  methods: {
    goToStep(step) {
      if (step >= 1 && step <= 3) {
        // Validate current step before allowing navigation
        if (step > this.currentStep && !this.canProceedToNextStep) {
          this.errors = this.getStepValidationErrors();
          this.$notify.error({
            title: 'Validation Error',
            message: 'Please complete the current step before proceeding'
          });
          return;
        }
        
        this.currentStep = step;
        this.errors = {}; // Clear errors when changing steps
      }
    },

    nextStep() {
      if (this.currentStep < 3) {
        if (!this.canProceedToNextStep) {
          this.errors = this.getStepValidationErrors();
          this.$notify.error({
            title: 'Validation Error',
            message: 'Please complete all required fields before proceeding'
          });
          return;
        }
        
        this.currentStep++;
        this.errors = {}; // Clear errors when proceeding
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    handleCopyToggle(elementType) {
      const selectedIdsKey = `selected_${elementType}_ids`;
      
      if (this.form[`copy_${elementType}`]) {
        // When enabling copy, select all items by default
        switch (elementType) {
          case 'tasks':
            this.form[selectedIdsKey] = this.ticket.tasks ? this.ticket.tasks.map(t => t.id) : [];
            break;
          case 'expenses':
            this.form[selectedIdsKey] = this.expenses ? this.expenses.map(e => e.id) : [];
            break;
          case 'notes':
            this.form[selectedIdsKey] = this.ticket.ticket_notes ? this.ticket.ticket_notes.map(n => n.id) : [];
            break;
          case 'replies':
            this.form[selectedIdsKey] = this.ticket.ticket_replies ? this.ticket.ticket_replies.map(r => r.id) : [];
            break;
          case 'forwarding_logs':
            this.form[selectedIdsKey] = this.ticket.ticket_forwarding_logs ? this.ticket.ticket_forwarding_logs.map(f => f.id) : [];
            break;
          case 'sms_logs':
            this.form[selectedIdsKey] = this.ticket.ticket_sms_logs ? this.ticket.ticket_sms_logs.map(s => s.id) : [];
            break;
          case 'whatsapp_logs':
            this.form[selectedIdsKey] = this.ticket.ticket_whatsapp_logs ? this.ticket.ticket_whatsapp_logs.map(w => w.id) : [];
            break;
          case 'inbound_emails':
            this.form[selectedIdsKey] = this.ticket.inbound_emails ? this.ticket.inbound_emails.map(e => e.id) : [];
            break;
          case 'inbound_text_messages':
            this.form[selectedIdsKey] = this.ticket.inbound_text_messages ? this.ticket.inbound_text_messages.map(m => m.id) : [];
            break;
          case 'inbound_whatsapp_messages':
            this.form[selectedIdsKey] = this.ticket.inbound_whatsapp_messages ? this.ticket.inbound_whatsapp_messages.map(m => m.id) : [];
            break;
        }
      } else {
        // When disabling copy, clear selected items
        this.form[selectedIdsKey] = [];
      }
    },

    formatDate(dateString) {
      return this.displayTimestamp(dateString);
    },

    clearError(field) {
      if (this.errors[field]) {
        this.$delete(this.errors, field);
      }
    },

    getStepValidationErrors() {
      switch (this.currentStep) {
        case 1:
          const errors = {};
          if (!this.form.subject) {
            errors.subject = ['Subject is required'];
          }
          if (!this.form.description) {
            errors.description = ['Description is required'];
          }
          return errors;
        case 2:
        case 3:
          return {}; // These steps are optional
        default:
          return {};
      }
    },

    getSelectedIdsForType(type) {
      switch (type) {
        case 'note':
          return this.form.selected_note_ids;
        case 'reply':
          return this.form.selected_reply_ids;
        case 'forwarding_log':
          return this.form.selected_forwarding_log_ids;
        case 'sms_log':
          return this.form.selected_sms_log_ids;
        case 'whatsapp_log':
          return this.form.selected_whatsapp_log_ids;
        case 'inbound_email':
          return this.form.selected_inbound_email_ids;
        case 'inbound_text_message':
          return this.form.selected_inbound_text_message_ids;
        case 'inbound_whatsapp_message':
          return this.form.selected_inbound_whatsapp_message_ids;
        case 'expense':
          return this.form.selected_expense_ids;
        default:
          return [];
      }
    },

    getIconForType(type) {
      switch (type) {
        case 'note':
          return 'fa fa-pencil-alt text-primary';
        case 'reply':
          return 'fa fa-reply text-primary';
        case 'forwarding_log':
          return 'fa fa-reply fa-flip-horizontal text-primary';
        case 'sms_log':
          return 'fa fa-comment-dots text-primary';
        case 'whatsapp_log':
          return 'fab fa-whatsapp text-primary';
        case 'inbound_email':
          return 'fa fa-envelope text-primary';
        case 'inbound_text_message':
          return 'fa fa-comment text-primary';
        case 'inbound_whatsapp_message':
          return 'fab fa-whatsapp text-primary';
        case 'expense':
          return 'fa fa-dollar-sign text-primary';
        default:
          return 'fa fa-circle text-primary';
      }
    },

    getTypeLabel(type) {
      switch (type) {
        case 'note':
          return 'Note';
        case 'reply':
          return 'Email Reply';
        case 'forwarding_log':
          return 'Forward';
        case 'sms_log':
          return 'SMS';
        case 'whatsapp_log':
          return 'WhatsApp';
        case 'inbound_email':
          return 'Inbound Email';
        case 'inbound_text_message':
          return 'Inbound SMS';
        case 'inbound_whatsapp_message':
          return 'Inbound WhatsApp';
        case 'expense':
          return 'Expense';
        default:
          return 'Unknown';
      }
    },

    getContentForType(type, object) {
      switch (type) {
        case 'note':
          return object.body;
        case 'reply':
          return object.subject;
        case 'forwarding_log':
          return object.subject;
        case 'sms_log':
          return object.body;
        case 'whatsapp_log':
          return object.body;
        case 'inbound_email':
          return object.subject;
        case 'inbound_text_message':
          return object.message;
        case 'inbound_whatsapp_message':
          return object.message;
        case 'expense':
          return `${object.description} - $${object.amount}`;
        default:
          return '';
      }
    },

    isItemSelected(type, id) {
      const selectedIds = this.getSelectedIdsForType(type);
      return selectedIds.includes(id);
    },

    toggleItemSelection(type, id) {
      const selectedIds = this.getSelectedIdsForType(type);
      const index = selectedIds.indexOf(id);
      
      if (index > -1) {
        selectedIds.splice(index, 1);
      } else {
        selectedIds.push(id);
      }

      // Update the corresponding copy flag based on whether any items of this type are selected
      this.updateCopyFlagForType(type);
    },

    updateCopyFlagForType(type) {
      const selectedIds = this.getSelectedIdsForType(type);
      const copyFlagKey = this.getCopyFlagKeyForType(type);
      
      if (copyFlagKey) {
        this.form[copyFlagKey] = selectedIds.length > 0;
      }
    },

    getCopyFlagKeyForType(type) {
      switch (type) {
        case 'note':
          return 'copy_notes';
        case 'reply':
          return 'copy_replies';
        case 'forwarding_log':
          return 'copy_forwarding_logs';
        case 'sms_log':
          return 'copy_sms_logs';
        case 'whatsapp_log':
          return 'copy_whatsapp_logs';
        case 'inbound_email':
          return 'copy_inbound_emails';
        case 'inbound_text_message':
          return 'copy_inbound_text_messages';
        case 'inbound_whatsapp_message':
          return 'copy_inbound_whatsapp_messages';
        case 'expense':
          return 'copy_expenses';
        default:
          return null;
      }
    },

    async splitTicket() {
      if (this.isSaving) return;

      this.isSaving = true;
      this.errors = {};

      // Always include all contacts
      this.form.copy_contacts = true;
      this.form.selected_contact_ids = this.ticket.contacts ? this.ticket.contacts.map(c => c.id) : [];

      // Set copy flags based on selected items
      this.form.copy_tasks = this.form.selected_task_ids.length > 0;
      this.form.copy_expenses = this.form.selected_expense_ids.length > 0;
      this.form.copy_notes = this.form.selected_note_ids.length > 0;
      this.form.copy_replies = this.form.selected_reply_ids.length > 0;
      this.form.copy_forwarding_logs = this.form.selected_forwarding_log_ids.length > 0;
      this.form.copy_sms_logs = this.form.selected_sms_log_ids.length > 0;
      this.form.copy_whatsapp_logs = this.form.selected_whatsapp_log_ids.length > 0;
      this.form.copy_inbound_emails = this.form.selected_inbound_email_ids.length > 0;
      this.form.copy_inbound_text_messages = this.form.selected_inbound_text_message_ids.length > 0;
      this.form.copy_inbound_whatsapp_messages = this.form.selected_inbound_whatsapp_message_ids.length > 0;

      try {
        const response = await axios.post(
          `${API_BASE}/tickets/${this.ticket.id}/split`,
          this.form
        );

        this.$bvModal.hide('split-ticket-modal');
        
        this.$notify.success({
          title: 'Success!',
          message: 'Ticket split successfully'
        });

        // Navigate to the new ticket
        if (response.data.new_ticket_id) {
          this.$router.push(`/tickets/${response.data.new_ticket_id}`);
        }
      } catch (error) {
        this.isSaving = false;
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.notifyErrors(error);
        }
      }
    },

    resetForm() {
      this.currentStep = 1;
      this.form = {
        subject: '',
        description: '',
        copy_contacts: true,
        copy_tasks: false,
        copy_expenses: false,
        copy_notes: false,
        copy_replies: false,
        copy_forwarding_logs: false,
        copy_sms_logs: false,
        copy_whatsapp_logs: false,
        copy_inbound_emails: false,
        copy_inbound_text_messages: false,
        copy_inbound_whatsapp_messages: false,
        selected_contact_ids: [],
        selected_task_ids: [],
        selected_expense_ids: [],
        selected_note_ids: [],
        selected_reply_ids: [],
        selected_forwarding_log_ids: [],
        selected_sms_log_ids: [],
        selected_whatsapp_log_ids: [],
        selected_inbound_email_ids: [],
        selected_inbound_text_message_ids: [],
        selected_inbound_whatsapp_message_ids: []
      };
      this.errors = {};
      this.isSaving = false;
    },

    getTimelineComponent(type) {
      switch (type) {
        case 'note': return 'TicketNote';
        case 'reply': return 'TicketReply';
        case 'forwarding_log': return 'TicketForwardingLog';
        case 'sms_log': return 'TicketSmsLog';
        case 'whatsapp_log': return 'TicketWhatsappLog';
        case 'inbound_email': return 'InboundEmail';
        case 'inbound_text_message': return 'TicketInboundTextMessage';
        case 'inbound_whatsapp_message': return 'TicketInboundWhatsappMessage';
        case 'expense': return 'TicketExpense';
        default: return null;
      }
    },

    getTimelineProps(item) {
      switch (item.type) {
        case 'note': return { note: item.object };
        case 'reply': return { reply: item.object };
        case 'forwarding_log': return { forwardingLog: item.object };
        case 'sms_log': return { smsLog: item.object };
        case 'whatsapp_log': return { whatsappLog: item.object };
        case 'inbound_email': return { inboundEmail: item.object };
        case 'inbound_text_message': return { inboundTextMessage: item.object };
        case 'inbound_whatsapp_message': return { inboundWhatsappMessage: item.object };
        case 'expense': return { expense: item.object };
        default: return {};
      }
    },

    getSelectionSummary() {
      const summary = {};
      
      // Count selected items for each type
      const types = ['note', 'reply', 'forwarding_log', 'sms_log', 'whatsapp_log', 
                     'inbound_email', 'inbound_text_message', 'inbound_whatsapp_message', 'expense'];
      
      types.forEach(type => {
        const selectedIds = this.getSelectedIdsForType(type);
        if (selectedIds.length > 0) {
          summary[type] = selectedIds.length;
        }
      });
      
      return summary;
    },

    updateTaskCopyFlag() {
      // Update the copy_tasks flag based on whether any tasks are selected
      this.form.copy_tasks = this.form.selected_task_ids.length > 0;
    },

    buildTimelineObject(obj) {
      // Create a copy of the object to avoid mutating the original
      const timelineObj = { ...obj };
      
      // Check if this object has split tracking properties
      if (obj.split_to_ticket_id && obj.split_at && obj.split_by_user_id) {
        timelineObj.split_tracking = {
          split_to_ticket_id: obj.split_to_ticket_id,
          split_at: obj.split_at,
          split_by_user_id: obj.split_by_user_id
        };
      }
      
      return timelineObj;
    }
  }
};
</script>

<style lang="scss">
#split-ticket-modal {
  .split-ticket-form {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    
    .form-group {
      label {
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
      }

      .form-control {
        border-radius: 0.5rem;
        border: 1px solid #ced4da;
        padding: 0.75rem 1rem;
        // font-size: 0.95rem;
        height: auto;
        transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;

        &:focus {
          border-color: #2a3042;
          box-shadow: 0 0 0 0.2rem rgba(42, 48, 66, 0.25);
        }

        &.is-invalid {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
        }
      }

      .invalid-feedback {
        display: block;
        color: #dc3545;
        font-size: 0.875rem;
        margin-top: 0.25rem;
      }

      .form-check-input:checked {
        background-color: #2a3042;
        border-color: #2a3042;
      }
    }
  }

  .toolbar {
    border-top: 1px #ebeff3 solid !important;
    border-bottom: 1px #cfd7df solid !important;
    padding-top: 11px;
    padding-bottom: 11px;
    box-shadow: 0 2px 4px 0 rgba(24, 50, 71, 0.08);
    z-index: 1;

    .btn-group {
      .btn {
        border-radius: 0.375rem;
        margin-right: 0.5rem;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }

  .modal-dialog {
    max-width: 100vw;
    width: calc(100vw);
    margin: auto;
    transform: none;

    .modal-body {
      padding: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
      max-height: calc(100vh - 127px);

      .tab-content {
        flex: 1;
        overflow-y: auto;
      }

    //   .modal-footer {
        // flex-shrink: 0;
    //   }
    }

    .modal-header {
      border-bottom: none;
      background: #2a3042;
      padding-top: 1rem;
      padding-bottom: 1rem;
      padding-left: 1.2rem;
      padding-right: 1.2rem;

      .modal-title {
        color: #fff !important;
      }

      .close {
        // hide the close button
        // display: none;
        // make it not clickable
        // pointer-events: none;
        filter: invert(1);
        padding-top: 18px;
        padding-right: 20px;
      }
    }

    .modal-content {
      border: 0;
      min-height: calc(100vh - 300px);
      height: calc(100vh);
      max-height: calc(100vh);
    }
  }

  .modal-footer {
    border-top: 1px solid #dee2e6;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  // Footer button styling
  .modal-footer .btn {
    min-width: 100px;
  }

  // Customer contacts styling
  .card {
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .card-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem 0.5rem 0 0;

      h6 {
        margin: 0;
        font-weight: 600;
        color: #495057;
      }

      .btn-link {
        color: #6c757d;
        text-decoration: none;
        transition: color 0.15s ease-in-out;

        &:hover {
          color: #495057;
        }

        &:focus {
          box-shadow: none;
        }

        i {
          font-size: 0.875rem;
          transition: transform 0.15s ease-in-out;
        }
      }
    }

    .card-body {
      padding: 1rem;
    }
  }

  .collapsed-card-header {
    border-bottom: 0 !important;
    border-radius: 0.5rem !important;
  }

  .form-check {
    margin-bottom: 0;
    
    .form-check-input {
      margin-right: 0.5rem;
    }
    
    .form-check-label {
      font-size: 0.875rem;
      color: #6c757d;
      margin-bottom: 0;
    }
  }

  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }

  .gap-2 {
    gap: 0.5rem;
  }

  .timeline-item-wrapper {
    margin-bottom: 0.1rem;
    padding: 0;
    border: none;
    background: none;
    transition: none;
  }

  .selection-summary {
    border: 1px solid #dee2e6;
    
    h6 {
      color: #495057;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
    
    .text-muted {
      font-size: 0.875rem;
      
      i {
        margin-right: 0.25rem;
      }
    }
  }
}
</style>